'use client';

import { FC, ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './CompanySetupModal.module.scss';
interface CompanySetupModalProps {
  show: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Extra class on the dialog box (sets width/height per popup). */
  dialogClassName?: string;
  /** Close when clicking the backdrop (default true). */
  backdropClose?: boolean;
}

/**
 * Bare-bones centered modal used by the company-setup popups. The Figma
 * popups have bespoke rounded corners/shadows that don't match the
 * bootstrap Modal skin, so each popup styles its own dialog box.
 */
const CompanySetupModal: FC<CompanySetupModalProps> = ({
  show,
  onClose,
  children,
  dialogClassName,
  backdropClose = true
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!show) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // The page scrolls on <html> (globals.css clips its overflow-x, so
    // body's overflow isn't passed up to the viewport); lock both.
    const root = document.documentElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    document.body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      root.style.overflow = previousRootOverflow;
    };
  }, [show, onClose]);
  if (!mounted || !show) return null;
  return createPortal(<div className={styles.backdrop} onMouseDown={event => {
    if (backdropClose && event.target === event.currentTarget) {
      onClose();
    }
  }}>
            <div role="dialog" aria-modal="true" className={`${styles.dialog} ${dialogClassName || ''}`}>
                {children}
            </div>
        </div>, document.body);
};
export default CompanySetupModal;